import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';
import axios from 'axios';
import dayjs from 'dayjs';
import {
    Input,
    Button,
    Flex,
    Space,
    Typography,
    Layout,
    Divider,
    Spin,
    Checkbox,
    Result,
    Alert,
    FloatButton
} from 'antd';
import {
    LoadingOutlined,
    ArrowDownOutlined
} from '@ant-design/icons';
import Centered from '../../components/Centered'

const { Content } = Layout;

const contentStyle = {
    marginTop: '4rem',
    marginBottom: '4rem',
    marginLeft: 'auto',
    marginRight: 'auto',
    maxWidth: 900,
}

export default function Submission() {
    const { uuid } = useParams();
    const [content, setContent] = useState('');
    const [isExpired, setIsExpired] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [canSubmit, setCanSubmit] = useState(false);
    const [layoutPlaceholders, setLayoutPlaceholders] = useState(null);
    const inputRef = useRef(null);
    const bottomRef = useRef(null);
    /**
     * Envia a requisição para recuperar os atributos
     * iniciais do relatório e da manifestação.
     * 
     * @param {String} reportUUID
     */
    async function fetchReportSubmission(reportUUID) {
        setIsFetching(true);

        try {
            const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/manifestacao/${reportUUID}`);

            setLayoutPlaceholders(data);
            setContent(data.manifestacao);
        } catch (error) {
            if (error?.status === 404) {
                setIsExpired(true);
            }
        }

        setIsFetching(false);
    }

    useEffect(() => {
        document.title = 'DPEMT - Manifestação RMA';

        if (uuid) {
            fetchReportSubmission(uuid);
        }
    }, [uuid]);

    /**
     * Envia a requisição para salvar a
     * manifestação do defensor.
     */
    async function handleSave() {
        // se o campo de justificativa não foi preenchido:
        if (!content?.trim().length) {
            inputRef.current.focus();

            return;
        }

        setIsSaving(true);

        try {
            await axios.post(`${process.env.REACT_APP_API_URL}/api/v1/manifestacao/${uuid}`, {
                content
            });

            setLayoutPlaceholders({
                ...layoutPlaceholders,
                submetido_em: dayjs()
            });
        } catch {}

        setIsSaving(false);
    }

    if (isExpired) {
        return (
            <Centered>
                <Result
                    status={404}
                    subTitle={(
                        <>
                            <Typography.Title level={4} style={{ marginBottom: 5 }}>
                                Este relatório não aceita manifestações.
                            </Typography.Title>

                            <Typography.Text>
                                Você ainda poderá visualizar seu conteúdo <a href={`${process.env.REACT_APP_API_URL}/api/v1/meu-relatorio/${uuid}`} target="_blank">clicando aqui</a>.
                            </Typography.Text>
                        </>
                    )}
                    extra={
                        <Typography.Text type="secondary">
                            Se acredita ser um erro, entre em contato com <a href="mailto:suportesistemas@dp.mt.gov.br">suportesistemas@dp.mt.gov.br</a>
                        </Typography.Text>
                    }
                />
            </Centered>
        );
    }

    return isFetching ? (
        <Centered>
            <Space direction="vertical" align="center" size="large">
                <Spin indicator={<LoadingOutlined spin />} size="large" />

                <Typography.Title level={5}>
                    Em alguns instantes os dados do relatório aparecerão aqui.
                </Typography.Title>
            </Space>
        </Centered>
    ) : (
        <>
            <FloatButton
                className="centered-anchor--sm"
                shape="circle"
                type="primary"
                icon={<ArrowDownOutlined />}
                onClick={() => {
                    bottomRef.current.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                        inline: "center",
                    });
                }}
            />

            <iframe
                src={`${process.env.REACT_APP_API_URL}/api/v1/meu-relatorio/${uuid}`}
                width="100%"
                height="700px"
            />

            <Content style={contentStyle}>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Typography.Title level={3}>
                        Dr(a). {layoutPlaceholders?.defensor},
                    </Typography.Title>

                    <Typography.Text style={{ fontSize: 18 }}>
                        {layoutPlaceholders?.submetido_em ? (
                            <>
                                Sua justificativa sobre o Relatório Mensal de Atividades (RMA) referente ao mês de <b>{layoutPlaceholders?.mes_referencia}</b> foi enviada para o <b>Gabinete da {layoutPlaceholders?.gabinete}</b>.
                            </>
                        ) : (
                            <>
                                O <b>Gabinete da {layoutPlaceholders?.gabinete}</b> solicita que, no prazo de <b><u>5 (cinco) dias corridos</u></b>, seja apresentada manifestação sobre o Relatório Mensal de Atividades (RMA) referente ao mês de <b>{layoutPlaceholders?.mes_referencia}</b>, apresentado acima.
                            </>
                        )}

                        <br /><br/>

                        Dúvidas poderão ser encaminhadas através do e-mail <a href={`mailto:${layoutPlaceholders?.gabinete_contato.email}`}>{layoutPlaceholders?.gabinete_contato.email}</a> ou pelo telefone funcional <b>{layoutPlaceholders?.gabinete_contato.phone_number}</b>.
                    </Typography.Text>

                    <Divider />

                    <Space size="small" direction="vertical" style={{ width: '100%' }}>
                        <Typography.Text style={{ fontSize: 18 }}>Justificativa:</Typography.Text>

                        <Input.TextArea
                            ref={inputRef}
                            rows={8}
                            placeholder="Utilize este espaço para escrever sua justificativa..."
                            onChange={({ target }) => setContent(target.value)}
                            value={content}
                            disabled={layoutPlaceholders?.submetido_em}
                        />

                        {layoutPlaceholders?.submetido_em && (
                            <Flex justify="flex-end">
                                <Typography.Text type="secondary">
                                    <small>Enviado em <b>{dayjs(layoutPlaceholders.submetido_em).format('DD/MM/YYYY HH:mm:ss')}</b></small>
                                </Typography.Text>
                            </Flex>
                        )}
                    </Space>

                    {!layoutPlaceholders?.submetido_em && (
                        <Flex className="submit-row--sm" justify="space-between">
                            <Space>
                                <Checkbox onChange={({ target }) => setCanSubmit(target.checked)} />

                                Estou ciente de que não será possível alterar a justificativa.
                            </Space>

                            <Button
                                type="primary"
                                onClick={handleSave}
                                loading={isSaving}
                                disabled={!canSubmit}
                            >
                                Enviar Justificativa
                            </Button>
                        </Flex>
                    )}

                    <div>
                        {layoutPlaceholders?.expira_em && (
                            <Alert
                                banner
                                type="warning"
                                description={`Esta página ficará INDISPONÍVEL após o dia ${dayjs(layoutPlaceholders?.expira_em).format('DD/MM/YYYY HH:mm:ss')}`}
                            />
                        )}

                        <Divider />
                    </div>
                </Space>

                <Flex justify="center">
                    <Typography.Text>
                        <small>Problemas na exibição desta página poderão ser relatados através do e-mail <a href="mailto:suportesistemas@dp.mt.gov.br">suportesistemas@dp.mt.gov.br</a></small>
                    </Typography.Text>
                </Flex>
            </Content>

            {/* Ancora para o final da página */}
            <div ref={bottomRef}></div>
        </>
    );
}
